'use client'

import { useState } from 'react'
import { ChevronRight, Map, Lightbulb, Award, X } from 'lucide-react'

interface CareerSidebarCardProps {
  careerData?: {
    salaryData: Array<{ level: string; salary: string; experience: string }>
    foundationData: {
      goals: string[]
      activities: string[]
      milestones: string[]
    }
    industryInsights: {
      marketAnalysis: { title: string; content: string }
      marketTrends: { title: string; content: string }
      nigeriaOpportunities: { title: string; content: string }
      futureTrends: { title: string; content: string }
    }
    certifications: Array<{
      title: string
      duration: string
      level: string
      description: string
    }>
  }
}

export function CareerSidebarCard({ careerData }: CareerSidebarCardProps) {
  const [activeTab, setActiveTab] = useState<string | null>(null)

  // Default data in case no props are passed
  const defaultData = {
    salaryData: [
      { level: "Entry-Level", salary: "₦60,000 - ₦120,000", experience: "0-2 years" },
      { level: "Mid-Level", salary: "₦150,000 - ₦350,000", experience: "3-7 years" },
      { level: "Senior-Level", salary: "₦400,000 - ₦800,000+", experience: "8+ years" },
    ],
    foundationData: {
      goals: [
        "Complete secondary education with focus on relevant subjects",
        "Develop basic cooking skills and food safety knowledge",
        "Build foundation in nutrition and food science",
        "Understand kitchen operations and equipment",
      ],
      activities: [
        "Participate in Home Economics practical sessions",
        "Join cooking clubs or workshops",
        "Assist in family meal preparation",
        "Learn about Nigerian cuisine basics",
      ],
      milestones: [
        "Food Safety Certificate",
        "Basic Cooking Competency",
        "Understanding of Nutrition Principles",
        "Kitchen Equipment Proficiency",
      ],
    },
    industryInsights: {
      marketAnalysis: {
        title: "Job Market Analysis",
        content: "High demand for skilled chefs in hotels, restaurants, and catering services. Growing opportunities in private chef services and food entrepreneurship."
      },
      marketTrends: {
        title: "Market Trends",
        content: "Rise in fusion cuisine, increased focus on healthy eating, growth in food delivery services, and demand for authentic Nigerian cuisine experiences."
      },
      nigeriaOpportunities: {
        title: "Nigeria Opportunities",
        content: "Expanding hospitality sector in major cities, growing middle class dining out, increased tourism, and opportunities in Nigerian cuisine globalization."
      },
      futureTrends: {
        title: "Future Trends & Challenges",
        content: "Sustainability focus, technology integration in kitchens, supply chain challenges, need for continuous skill upgrading, and global competition."
      },
    },
    certifications: [
      {
        title: "Food Safety and Hygiene Certificate",
        duration: "2-4 weeks",
        level: "Beginner",
        description: "Essential food handling and safety practices certification"
      },
      {
        title: "Professional Chef Diploma",
        duration: "6-12 months",
        level: "Intermediate",
        description: "Comprehensive culinary arts training program"
      },
      {
        title: "Certified Executive Chef (CEC)",
        duration: "2-3 years",
        level: "Expert",
        description: "Advanced certification for kitchen leadership and management"
      },
      {
        title: "Nigerian Cuisine Specialist",
        duration: "3-6 months",
        level: "Intermediate",
        description: "Specialization in traditional and modern Nigerian cooking"
      },
    ]
  }

  const data = careerData || defaultData

  const cards = [
    {
      icon: <Map className="w-5 h-5" />,
      title: "Career Map",
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200"
    },
    {
      icon: <Lightbulb className="w-5 h-5" />,
      title: "Industry Insights",
      color: "text-teal-600",
      bgColor: "bg-teal-50",
      borderColor: "border-teal-200"
    },
    {
      icon: <Award className="w-5 h-5" />,
      title: "Certifications",
      color: "text-amber-600",
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200"
    },
  ]

  const closeModal = () => {
    setActiveTab(null)
  }

  const renderCareerMapContent = () => (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-bold text-slate-800 mb-4">Foundation (Current - SS3)</h3>
        <div className="grid grid-cols-1 gap-4">
          <div className="bg-slate-50 p-4 rounded-lg">
            <h4 className="font-bold text-base text-blue-600 mb-3">Goals</h4>
            <ul className="space-y-2">
              {data.foundationData.goals.map((goal, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-slate-700">
                  <span className="text-blue-500 mt-1 flex-shrink-0">•</span>
                  <span>{goal}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-slate-50 p-4 rounded-lg">
            <h4 className="font-bold text-base text-green-600 mb-3">Key Activities</h4>
            <ul className="space-y-2">
              {data.foundationData.activities.map((activity, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-slate-700">
                  <span className="text-green-500 mt-1 flex-shrink-0">•</span>
                  <span>{activity}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-slate-50 p-4 rounded-lg">
            <h4 className="font-bold text-base text-purple-600 mb-3">Milestones</h4>
            <ul className="space-y-2">
              {data.foundationData.milestones.map((milestone, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-slate-700">
                  <span className="text-purple-500 mt-1 flex-shrink-0">•</span>
                  <span>{milestone}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl">
        <h3 className="text-lg font-bold text-slate-800 mb-3">Salary Projections</h3>
        <div className="grid grid-cols-1 gap-3">
          {data.salaryData.map((salary, index) => (
            <div key={index} className="bg-white p-3 rounded-lg shadow-sm">
              <h4 className="font-bold text-base text-slate-800 mb-1">{salary.level}</h4>
              <p className="text-lg font-bold text-blue-600 mb-1">{salary.salary}</p>
              <p className="text-xs text-slate-600">Experience: {salary.experience}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )

  const renderIndustryInsightsContent = () => (
    <div className="grid grid-cols-1 gap-4">
      <div className="bg-slate-50 p-4 rounded-lg">
        <h3 className="text-base font-bold text-slate-800 mb-2">{data.industryInsights.marketAnalysis.title}</h3>
        <p className="text-sm text-slate-700">{data.industryInsights.marketAnalysis.content}</p>
      </div>

      <div className="bg-slate-50 p-4 rounded-lg">
        <h3 className="text-base font-bold text-slate-800 mb-2">{data.industryInsights.marketTrends.title}</h3>
        <p className="text-sm text-slate-700">{data.industryInsights.marketTrends.content}</p>
      </div>

      <div className="bg-slate-50 p-4 rounded-lg">
        <h3 className="text-base font-bold text-slate-800 mb-2">{data.industryInsights.nigeriaOpportunities.title}</h3>
        <p className="text-sm text-slate-700">{data.industryInsights.nigeriaOpportunities.content}</p>
      </div>

      <div className="bg-slate-50 p-4 rounded-lg">
        <h3 className="text-base font-bold text-slate-800 mb-2">{data.industryInsights.futureTrends.title}</h3>
        <p className="text-sm text-slate-700">{data.industryInsights.futureTrends.content}</p>
      </div>
    </div>
  )

  const renderCertificationsContent = () => (
    <div className="grid grid-cols-1 gap-4">
      {data.certifications.map((certification, index) => (
        <div key={index} className="bg-slate-50 p-4 rounded-lg border border-slate-200">
          <div className="flex flex-col gap-3">
            <div className="flex-1">
              <h3 className="text-lg font-bold text-slate-800 mb-1">{certification.title}</h3>
              <p className="text-sm text-slate-600 mb-2">{certification.description}</p>

              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-700 font-medium">{certification.duration}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`font-medium px-2 py-1 rounded-full text-xs ${
                      certification.level === "Beginner"
                        ? "bg-green-100 text-green-800"
                        : certification.level === "Intermediate"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-purple-100 text-purple-800"
                    }`}
                  >
                    {certification.level}
                  </span>
                </div>
              </div>
            </div>

            <button className="bg-amber-600 hover:bg-amber-700 text-white font-medium py-2 px-4 rounded-lg transition-colors whitespace-nowrap text-sm w-full">
              Get Certified
            </button>
          </div>
        </div>
      ))}
    </div>
  )

  return (
    <>
      <div className="space-y-3">
        {cards.map((card) => (
          <button
            key={card.title}
            onClick={() => setActiveTab(card.title)}
            className={`flex items-center justify-between p-4 rounded-xl ${card.bgColor} hover:opacity-90 transition-all border ${card.borderColor} w-full text-left`}
          >
            <div className="flex items-center gap-3">
              <span className={card.color}>{card.icon}</span>
              <span className="font-semibold text-gray-800 text-sm">{card.title}</span>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </button>
        ))}
      </div>

      {/* Modal for Active Tab */}
      {activeTab && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${
                  activeTab === "Career Map" ? "bg-blue-100" :
                  activeTab === "Industry Insights" ? "bg-teal-100" :
                  "bg-amber-100"
                }`}>
                  {cards.find(card => card.title === activeTab)?.icon}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">{activeTab}</h2>
                  <p className="text-sm text-slate-500">Quick Overview</p>
                </div>
              </div>
              
              <button
                onClick={closeModal}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Close modal"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {activeTab === "Career Map" && renderCareerMapContent()}
              {activeTab === "Industry Insights" && renderIndustryInsightsContent()}
              {activeTab === "Certifications" && renderCertificationsContent()}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t border-slate-200 bg-slate-50">
              <div className="text-sm text-slate-500">
                Detailed view from main section
              </div>
              <button
                onClick={closeModal}
                className="px-4 py-2 text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}